/* eslint-disable indent */
import moment from 'moment';

import PropTypes from 'prop-types';

import React from 'react';
import { connect } from 'react-redux';

import { createStructuredSelector } from 'reselect';

import SelectAutoComplete from 'atoms/SelectAutoComplete';
import SmallLoadingIndicator from 'atoms/SmallLoadingIndicator';
import GroupAttendance, { buildGroupedOptions, GroupOption } from 'components/GroupAttendance';
import { generateToast, loadClinicians } from 'containers/App/actions';
import { makeSelectAuth, makeSelectClinicians } from 'containers/App/selectors';

import {
	deleteSessions,
	getAttendance,
	getSessions,
	loadPrograms,
	saveAttendance,
} from './actions';
import './index.css';
import makeSelectAttendance, {
	makeSelectAllPrograms,
	makeSelectGetAttendance,
	makeSelectGetSessions,
	makeSelectLoadingPrograms,
} from './selectors';

/* eslint-disable react/prefer-stateless-function */
export class Attendance extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			selectedProgram: {},
			isSaving: false,
		};
	}

	componentDidMount() {
		const { fetchProgram, dispatch, auth } = this.props;
		const { loggedInUser } = auth;
		fetchProgram(loggedInUser?.roleName, loggedInUser?.id);
		dispatch(loadClinicians());
	}

	componentDidUpdate() {
		const { selectedProgram } = this.state;
		const { programs, fetchAttendance, fetchSessions } = this.props;

		if (!selectedProgram.value && programs.length > 0) {
			const [program] = programs;
			this.setState({
				selectedProgram: {
					value: program.groupName,
					label: program.groupName,
					program,
				},
			});
			fetchAttendance({
				programId: program.programId,
				month: moment().format('M'),
				year: moment().format('YYYY'),
			});
			fetchSessions({
				programId: program.programId,
			});
		}
	}

	getOptions = () => {
		let { programs } = this.props;

		if (!Array.isArray(programs)) {
			programs = [];
		}

		// Build grouped options with section headers
		const groupedOptions = buildGroupedOptions(programs);

		// If buildGroupedOptions returned grouped data, use it; otherwise fall back
		if (groupedOptions.length > 0 && groupedOptions.some((opt) => opt.isHeader)) {
			// Map the grouped options to include the full program object
			return groupedOptions.map((opt) => {
				if (opt.isHeader) {
					return opt;
				}
				const program = programs.find((p) => p.programId === opt.value);
				return {
					...opt,
					value: program?.groupName || opt.label,
					program,
				};
			});
		}

		// Fallback to simple list
		return programs.map((program) => ({
			value: program.groupName,
			label: program.groupName,
			program,
		}));
	};

	handleProgramChange = (program) => {
		const { fetchAttendance, fetchSessions } = this.props;
		this.setState({ selectedProgram: program });
		fetchAttendance({
			programId: program.program.programId,
			month: moment().format('M'),
			year: moment().format('YYYY'),
		});
		fetchSessions({
			programId: program.program.programId,
		});
	};

	saveAttendance = (data) => {
		const { doSaveAttendance, createToast } = this.props;
		this.setState({ isSaving: true });
		doSaveAttendance(
			data.attendance,
			() => {
				createToast({
					text: 'Attendance Marked Successfully',
					type: 'custom-success',
				});
				this.setState({ isSaving: false });
			},
			() => {
				createToast({
					text: 'Attendance Could Not Be Taken',
					position: 'bottom-left',
					type: 'custom-error',
				});
				this.setState({ isSaving: false });
			},
		);
	};

	render() {
		const { selectedProgram, isSaving } = this.state;
		const {
			fetchAttendance,
			attendances,
			fetchSessions,
			sessions,
			type,
			doDeleteSessions,
			auth,
			loadingPrograms,
			clinicians = [],
		} = this.props;
		const options = this.getOptions();
		return (
			<div className="container-fluid px-5 mt-5">
				<h6 className="text-dark cust-clinician-attendance-heading font-10 text-uppercase">
					<strong>Attendance</strong>
				</h6>
				<div className="col-12 p-3 mb-3 d-flex justify-content-between filter-nav bg-white">
					<div style={{ width: '20rem' }}>
						{loadingPrograms ? (
							<div className="border">
								<SmallLoadingIndicator
									color="var(--main-color)"
									fontSize="20px"
									style={{ height: '20px', margin: '0px 8px' }}
									className="my-0"
									text="Loading Therapies"
									inline
								/>
							</div>
						) : (
							<SelectAutoComplete
								handleChange={this.handleProgramChange}
								options={options}
								value={selectedProgram}
								placeholder="Select Therapy"
								indicatorClass="fas fa-sort dropdown-indicator text-primary"
								optionStyles={{ fontSize: '14px' }}
								singleValueStyles={{
									color: 'var(--main-color)',
									fontSize: '14px',
								}}
								components={{ Option: GroupOption }}
							/>
						)}
					</div>
				</div>
				{selectedProgram.program && (
					<GroupAttendance
						auth={auth}
						type={type}
						programDetails={selectedProgram.program}
						attendance={attendances}
						fetchAttendance={fetchAttendance}
						saveAttendance={this.saveAttendance}
						sessions={sessions}
						fetchSessions={fetchSessions}
						toggleShowAddSession={this.toggleShowAddSession}
						deleteSessions={doDeleteSessions}
						isSaving={isSaving}
						clinicians={clinicians}
					/>
				)}
			</div>
		);
	}
}

Attendance.propTypes = {
	type: PropTypes.string.isRequired,
	fetchAttendance: PropTypes.func,
	doUpdateAttendance: PropTypes.func,
	fetchProgram: PropTypes.func,
	attendances: PropTypes.array,
	fetchSessions: PropTypes.func,
	sessions: PropTypes.array,
	programs: PropTypes.any,
};

const mapStateToProps = createStructuredSelector({
	attendance: makeSelectAttendance(),
	attendances: makeSelectGetAttendance(),
	programs: makeSelectAllPrograms(),
	sessions: makeSelectGetSessions(),
	loadingPrograms: makeSelectLoadingPrograms(),
	auth: makeSelectAuth(),
	clinicians: makeSelectClinicians(),
});

function mapDispatchToProps(dispatch) {
	return {
		dispatch,
		fetchAttendance: (data, cb) => dispatch(getAttendance(data, cb)),
		doSaveAttendance: (data, success, err) =>
			dispatch(saveAttendance({ attendance: data }, success, err)),
		fetchSessions: (data) => dispatch(getSessions(data)),
		fetchProgram: (role, clinicianId) => dispatch(loadPrograms(role, clinicianId)),
		doDeleteSessions: (ids) => dispatch(deleteSessions(ids)),
		createToast: (data) => dispatch(generateToast(data)),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(Attendance);
